import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldCheck, Loader2, Save, Bot, Mic, MessageSquare,
  Wrench, DollarSign, AlertTriangle,
} from "lucide-react";

const SCOPE_META = {
  admin: { label: "Admin · Client Management", icon: ShieldCheck, color: "bg-zinc-900" },
  finance: { label: "Finance & Payroll", icon: DollarSign, color: "bg-amber-700" },
  client: { label: "Client Portal", icon: Bot, color: "bg-[#13B5EA]" },
};

/**
 * /admin/ai-access — single page driving every AI gate:
 *   • per-scope enabled flag
 *   • per-scope chat / voice mode toggles
 *   • per-tool toggles inside each scope
 *   • global monthly budget + alert threshold
 */
export default function AIAccessPage() {
  const [cfg, setCfg] = useState(null);
  const [registry, setRegistry] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ai-access");
      setCfg({ scopes: data.scopes, global: data.global });
      setRegistry(data.registry || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load AI access");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setScope = (scope, patch) => {
    setCfg((c) => ({ ...c, scopes: { ...c.scopes,
      [scope]: { ...c.scopes[scope], ...patch } } }));
  };
  const setTool = (scope, tool, on) => {
    setCfg((c) => ({ ...c, scopes: { ...c.scopes,
      [scope]: { ...c.scopes[scope],
        tools: { ...c.scopes[scope].tools, [tool]: on } } } }));
  };
  const setGlobal = (patch) => {
    setCfg((c) => ({ ...c, global: { ...c.global, ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/ai-access", {
        scopes: cfg.scopes,
        global: cfg.global,
      });
      toast.success("AI access settings saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading || !cfg) {
    return <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  }

  return (
    <div className="p-6 max-w-5xl" data-testid="ai-access-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-zinc-900 text-white">
          <Bot className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">ADMINISTRATOR</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            AI access control
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Master switches for every AI agent in the platform. Disable
            scopes, modes, tools, or set a hard monthly budget cap.
          </p>
        </div>
        <Button onClick={save} disabled={saving}
          className="rounded-sm" data-testid="ai-access-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <Save className="h-3.5 w-3.5 mr-1.5" />}
          Save changes
        </Button>
      </div>

      {/* ── Global budget ── */}
      <Card className="rounded-sm border-zinc-200 mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-4 w-4 text-emerald-700" />
            <div className="font-display font-bold text-sm">Global budget</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500">
              Monthly budget (USD)
              <Input type="number" step="1" min="0"
                value={cfg.global.monthly_budget_usd}
                onChange={(e) => setGlobal({ monthly_budget_usd: parseFloat(e.target.value) || 0 })}
                className="mt-1 h-9 text-sm font-mono"
                data-testid="ai-access-budget" />
            </label>
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500">
              Alert threshold (%)
              <Input type="number" step="1" min="0" max="100"
                value={cfg.global.alert_threshold_pct}
                onChange={(e) => setGlobal({ alert_threshold_pct: parseInt(e.target.value, 10) || 0 })}
                className="mt-1 h-9 text-sm font-mono"
                data-testid="ai-access-alert" />
            </label>
            <label className="flex items-center gap-2 mt-6 sm:mt-7">
              <Switch checked={cfg.global.hard_stop_over_budget}
                onCheckedChange={(v) => setGlobal({ hard_stop_over_budget: v })}
                data-testid="ai-access-hard-stop" />
              <span className="text-xs">
                <span className="font-semibold">Hard-stop</span>
                <span className="block text-[10px] text-zinc-500">
                  Block all AI calls when over budget
                </span>
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* ── Per-scope cards ── */}
      {Object.keys(SCOPE_META).map((scope) => {
        const s = cfg.scopes[scope] || {};
        const meta = SCOPE_META[scope];
        const Icon = meta.icon;
        const tools = registry[scope] || [];
        const disabled = !s.enabled;
        return (
          <Card key={scope} className="rounded-sm border-zinc-200 mb-3"
            data-testid={`ai-access-scope-${scope}`}>
            <CardContent className="p-0">
              {/* Scope header */}
              <div className={`px-4 py-3 ${meta.color} text-white flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <div className="font-display font-bold text-sm">{meta.label}</div>
                  {!s.enabled && (
                    <Badge variant="outline" className="rounded-sm text-[9px] bg-red-100 text-red-800 border-red-200 ml-1">
                      DISABLED
                    </Badge>
                  )}
                </div>
                <label className="flex items-center gap-2 text-xs">
                  Scope enabled
                  <Switch checked={!!s.enabled}
                    onCheckedChange={(v) => setScope(scope, { enabled: v })}
                    data-testid={`ai-access-${scope}-enabled`} />
                </label>
              </div>

              {/* Mode toggles */}
              <div className={`grid grid-cols-2 gap-3 p-4 border-b border-zinc-100 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
                <ToggleTile icon={MessageSquare} label="Chat (text)"
                  on={!!s.chat_enabled}
                  onChange={(v) => setScope(scope, { chat_enabled: v })}
                  testid={`ai-access-${scope}-chat`} />
                <ToggleTile icon={Mic} label="Voice (live talk)"
                  on={!!s.voice_enabled}
                  onChange={(v) => setScope(scope, { voice_enabled: v })}
                  testid={`ai-access-${scope}-voice`} />
              </div>

              {/* Tool toggles */}
              <div className={`p-4 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
                  <Wrench className="h-3 w-3" /> Tools / data sources
                </div>
                {renderToolGroups(tools, scope, s, setTool)}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Card className="rounded-sm border-amber-200 bg-amber-50 mt-4">
        <CardContent className="p-3 flex items-start gap-2 text-[11px] text-amber-900">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>How this affects users:</strong> disabling a scope blocks
            both the chat and voice widget for that section. Disabling an
            individual tool just hides that data source from the agent — it
            will tell users it doesn&apos;t have access if asked. The hard-stop
            budget flag, when on, returns a 403 on every call once monthly
            spend exceeds the budget (read from the AI Costs page).
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ToggleTile({ icon: Icon, label, on, onChange, testid }) {
  return (
    <label className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer ${
      on ? "border-zinc-300 bg-white" : "border-zinc-200 bg-zinc-50"
    }`}>
      <Icon className={`h-4 w-4 ${on ? "text-zinc-700" : "text-zinc-400"}`} />
      <span className="flex-1 text-sm font-semibold">{label}</span>
      <Switch checked={on} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

/**
 * Render scope tools grouped by their optional `category` field. Tools
 * with NO category fall into the implicit "Core" group rendered first.
 */
function renderToolGroups(tools, scope, s, setTool) {
  const grouped = new Map();
  grouped.set("Core", []);
  for (const t of tools) {
    const key = t.category || "Core";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(t);
  }
  return [...grouped.entries()]
    .filter(([, list]) => list.length > 0)
    .map(([category, list]) => (
      <div key={category} className="mb-4 last:mb-0"
        data-testid={`ai-access-${scope}-group-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {category !== "Core" && (
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-amber-700 mb-2 mt-1 flex items-center gap-1.5">
            <span className="h-1 w-1 rounded-full bg-amber-600" />
            {category}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {list.map((t) => (
            <label key={t.name}
              className="flex items-start gap-2 p-2 border border-zinc-200 rounded-sm hover:bg-zinc-50">
              <Switch checked={!!s.tools?.[t.name]}
                onCheckedChange={(v) => setTool(scope, t.name, v)}
                data-testid={`ai-access-${scope}-tool-${t.name}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold">{t.label}</div>
                <div className="text-[10px] text-zinc-500 leading-tight">{t.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    ));
}
